/**
 * Knowing when the video's ICE path moves, so the camera goes on after it has (see SipVideo.enableCamera).
 *
 * Turning the camera on over a video transceiver that is already negotiated - my own after camera-off, or the one the
 * other party's upgrade created - is a re-offer from this side. Chrome offers BUNDLE in it and gives the video
 * m-section the audio transport's ICE credentials. The media gateway does not bundle, so to it that is an ICE restart:
 * its answer restarts ICE on the video transport, and Chrome moves the video to a new candidate pair a few hundred
 * milliseconds later. The gateway drops what still arrives on the old pair. Sent in that gap, the camera's first frames
 * and Chrome's bandwidth probe are lost - the probe never gets a result, and the estimate starts at 300 kbps and takes
 * half a minute to reach 720p.
 */

/**
 * The ICE ufrag of the m-section with this mid - or the session-level one, which an m-section without its own inherits
 * @param sdp The session description
 * @param mid The mid of the m-section
 * @returns The ufrag, undefined when there is no SDP, no section with that mid, or no ufrag
 */
export function iceUfrag(sdp: string | undefined, mid: string | null): string | undefined {
	if (!sdp || mid === null) { return undefined }

	const eol = sdp.includes('\r\n') ? '\r\n' : '\n'
	const lines = sdp.split(eol)
	const ufragIn = (from: number, to: number) => lines.slice(from, to).find(line => line.startsWith('a=ice-ufrag:'))?.slice('a=ice-ufrag:'.length)
	const midLine = lines.indexOf(`a=mid:${mid}`)
	const start = lines.slice(0, midLine).map(line => line.startsWith('m=')).lastIndexOf(true)

	// No such mid, or an a=mid in the session part, before any m= line
	if (midLine === -1 || start === -1) { return undefined }

	const next = lines.findIndex((line, index) => index > midLine && line.startsWith('m='))
	const firstMedia = lines.findIndex(line => line.startsWith('m='))

	return ufragIn(start, next === -1 ? lines.length : next) ?? ufragIn(0, firstMedia)
}

/** The local and remote end of the transport's selected candidate pair, for telling whether it moved */
export function candidatePairKey(transport: RTCIceTransport | null | undefined): string | undefined {
	const pair = transport?.getSelectedCandidatePair?.()

	if (!pair) { return undefined }
	return `${pair.local.address ?? ''}:${pair.local.port ?? ''}>${pair.remote.address ?? ''}:${pair.remote.port ?? ''}`
}

/**
 * Wait for an ICE transport to select a new candidate pair
 * @param transport The ICE transport - nothing to wait for without one
 * @param timeoutMs How long to wait at most
 * @returns true when the pair changed, false on the timeout or without a transport
 */
export function nextCandidatePair(transport: RTCIceTransport | null | undefined, timeoutMs: number): Promise<boolean> {
	if (!transport) { return Promise.resolve(false) }

	return new Promise((resolve) => {
		const wait: { timer?: ReturnType<typeof setTimeout> } = {}
		const onChange = () => {
			clearTimeout(wait.timer)
			resolve(true)
		}

		transport.addEventListener('selectedcandidatepairchange', onChange, { once: true })
		wait.timer = setTimeout(() => {
			transport.removeEventListener('selectedcandidatepairchange', onChange)
			resolve(false)
		}, timeoutMs)
	})
}
